import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React from 'react';

import * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type { CustomHostnamePointer } from '@gitbook/app-database';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import { Button, LoadingIndicator } from '@gitbook/spine-react';

import { useSetAnalyticsScreen } from '../../navigation';
import { apiQuery } from '../../state';
import { CustomHostnameStatusAlert } from './CustomHostnameStatusAlert';
import { useCNAMERecord } from './useCNAMERecord';

interface GoLiveStepProps {
    /** Custom hostname user selected. */
    hostname: string;

    /** Loading value tracking retry process loading. */
    loading: boolean;

    /** Callback when clicking finish button. */
    onFinish: () => void;

    /** Callback triggered when custom hostname status is changed to 'live'. */
    onLive: () => void;

    /** Callback to restart the flow by going back to first step. */
    onRestart: () => void;

    /** Callback to reset the custom hostname status. */
    onRetry: () => void;

    /** Pointer to which to configure the domain. */
    pointer: CustomHostnamePointer;
}

const progressToIconMap: Record<'success' | 'error' | 'unknown', SVGIconComponent> = {
    success: Icon.CheckCircle,
    error: Icon.Warning,
    unknown: Icon.HelpCircle,
};

/**
 * UI used to display short info on dns, ssl, and overall custom domain setup status.
 */
function ProgressItem(props: {
    progress: 'pending' | 'success' | 'error' | 'unknown';
    info: React.ReactNode;
}): React.ReactElement {
    const { progress, info } = props;

    const progressToIconStyleMap = {
        success: 'text-success',
        pending: 'text-primary',
        unknown: 'text-muted',
        error: 'text-danger',
    };
    const progressToTextStyleMap = {
        success: 'text-base',
        pending: 'text-muted',
        unknown: 'text-muted',
        error: 'text-base',
    };
    const Icon = progressToIconMap[progress];

    return (
        <div className={clsx('flex items-center gap-4', progress === 'unknown' && 'opacity-50')}>
            {progress === 'pending' ? (
                <LoadingIndicator />
            ) : (
                <Icon className={clsx('size-4.5', progressToIconStyleMap[progress])} />
            )}

            <div className={clsx('text-ui-action', progressToTextStyleMap[progress])}>{info}</div>
        </div>
    );
}

/**
 * Displays dns status updates.
 */
function DNSStatus(props: { status: api.CustomHostnameStatus | undefined; hostname: string }) {
    const { status, hostname } = props;
    switch (status) {
        case api.CustomHostnameDnsStatus.DnsPassed:
        case api.CustomHostnameDnsStatus.DnsCloudflareProxied:
        case api.CustomHostnameSslStatus.Live:
        case api.CustomHostnameSslStatus.SslPending: // SSL related statuses where we also want to show dns step success
        case api.CustomHostnameSslStatus.SslFailed:
        case api.CustomHostnameSslStatus.SslRetryExpired:
        case api.CustomHostnameSslStatus.SslUnknown:
            return (
                <ProgressItem
                    progress="success"
                    info={
                        <>
                            Detected DNS for <b>{hostname}</b>
                        </>
                    }
                />
            );
        case api.CustomHostnameErrorStatus.InvalidDomain:
        case api.CustomHostnameDnsStatus.DnsWrongCname:
        case api.CustomHostnameDnsStatus.DnsNoCname:
        case api.CustomHostnameDnsStatus.DnsWrongCaa:
            return (
                <ProgressItem
                    progress="error"
                    info={
                        <>
                            Invalid configuration for <b>{hostname}</b>
                        </>
                    }
                />
            );
        default:
            return (
                <ProgressItem
                    progress="pending"
                    info={
                        <>
                            Detecting DNS for <b>{hostname}</b>
                        </>
                    }
                />
            );
    }
}

/**
 * Displays ssl status updates.
 */
function SSLStatus(props: { status: api.CustomHostnameStatus | undefined }) {
    const { status } = props;
    switch (status) {
        case api.CustomHostnameSslStatus.Live:
            return <ProgressItem progress="success" info="Configured SSL Certificate" />;
        case api.CustomHostnameSslStatus.SslFailed:
        case api.CustomHostnameSslStatus.SslRetryExpired:
            return <ProgressItem progress="error" info="Configuring SSL Certificate" />;
        case api.CustomHostnameSslStatus.SslPending:
        case api.CustomHostnameSslStatus.SslUnknown:
            return <ProgressItem progress="pending" info="Configuring SSL Certificate" />;
        default:
            return <ProgressItem progress="unknown" info="Configuring SSL Certificate" />;
    }
}

/**
 * Displays pending UI until domain is live.
 */
function DomainStatus(props: { status: api.CustomHostnameStatus | undefined }) {
    const { status } = props;
    switch (status) {
        case api.CustomHostnameSslStatus.Live:
            return <ProgressItem progress="success" info="Your domain is live and read to go!" />;
        case api.CustomHostnameSslStatus.SslPending:
        case api.CustomHostnameDnsStatus.DnsPassed:
        case api.CustomHostnameDnsStatus.DnsCloudflareProxied:
            return <ProgressItem progress="pending" info="Your domain status is pending" />;
        default:
            return <ProgressItem progress="unknown" info="Your domain status is pending" />;
    }
}

/**
 * Step 3: Step to review status and edit domain or try again if error occurred.
 */
export function GoLive(
    props: GoLiveStepProps & {
        /** Additional props need only for sites */
        cname: string;
    }
): React.ReactElement {
    const { hostname, loading, onRestart, onRetry, onLive, onFinish, cname } = props;

    const { data: customHostname } = useSuspenseQuery(
        apiQuery('getCustomHostname', { hostname }, { nullOnError: true })
    );
    const { status } = customHostname || {};

    React.useEffect(() => {
        if (status === api.CustomHostnameSslStatus.Live) {
            onLive();
        }
    }, [status, onLive]);

    return (
        <div className="flex flex-col">
            <div className="mt-4 flex flex-col items-center px-1 py-10">
                <p className="text-center text-ui-heading-large">
                    {status === api.CustomHostnameSslStatus.Live
                        ? 'Congrats your domain is live!'
                        : 'Setting up your custom domain'}
                </p>
            </div>
            <div className="mb-6 flex w-full max-w-[31.5rem] flex-col self-center">
                <>
                    <div className="mb-6 flex flex-col items-center gap-6">
                        <DNSStatus status={status} hostname={hostname} />
                        <SSLStatus status={status} />
                        <DomainStatus status={status} />
                    </div>

                    <CustomHostnameStatusAlert
                        hostname={hostname}
                        cname={cname}
                        showTitle={false}
                        actionOverride={
                            status === api.CustomHostnameDnsStatus.DnsWrongCname ||
                            status === api.CustomHostnameDnsStatus.DnsNoCname
                                ? {
                                      label: 'Try again',
                                      onPress: onRetry,
                                  }
                                : undefined
                        }
                    />
                </>
            </div>
            <div className="my-6 flex w-full max-w-[31.5rem] items-center justify-between gap-4 self-center">
                <Button
                    label="Edit domain"
                    onPress={onRestart}
                    kind="secondary"
                    className="flex-1"
                />
                <Button
                    label="Finish"
                    disabled={loading || status !== api.CustomHostnameSslStatus.Live}
                    onPress={onFinish}
                    kind="primary"
                    className="flex-1"
                />
            </div>
        </div>
    );
}

export function GoLiveStep(props: GoLiveStepProps): React.ReactElement {
    const cname = useCNAMERecord(props.pointer);

    return <GoLive cname={cname} {...props} />;
}

export function SiteGoLiveStep(
    props: GoLiveStepProps & {
        cname: string;
        analyticsContext: analytics.AnalyticsEventContext;
    }
): React.ReactElement {
    const { analyticsContext, ...rest } = props;
    useSetAnalyticsScreen(
        {
            ...analyticsContext,
            screen: 'site_custom_domain',
            screenTab: 'go_live',
        },
        [analyticsContext]
    );

    return <GoLive {...rest} />;
}
